/**
 * 사업자 크레딧 결제 목록 테이블 — PaymentList 와 동일한 스타일/패턴.
 *  컬럼: 사업자 / 원장 / 패키지 / 결제금액 / 크레딧 / 상태 / 수단 / 결제일시 / 관리(취소+영수증)
 *  취소 노출 조건: canCancel === true (BE 가 7일 이내 + 미사용 + COMPLETED 만 true 로 응답)
 */
import { useState } from 'react';
import type { CreditPaymentItem } from '../../types/payment';
import { paymentsApi } from '../../services/api/payments';

interface Props {
  payments: CreditPaymentItem[];
  onCancelled?: () => void;  // 취소 성공 시 부모 refresh
}

const STATUS_META: Record<string, { label: string; cls: string }> = {
  COMPLETED:        { label: '완료',       cls: 'bg-green-100 text-green-800' },
  CANCELLED:        { label: '취소',       cls: 'bg-red-100 text-red-800' },
  REFUNDED:         { label: '환불',       cls: 'bg-gray-100 text-gray-600' },
  PARTIAL_REFUNDED: { label: '부분 환불',  cls: 'bg-yellow-100 text-yellow-900' },
};

const formatDateTime = (s: string): string => {
  try {
    return new Date(s).toLocaleString('ko-KR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return s; }
};

export function CreditPaymentList({ payments, onCancelled }: Props) {
  const [cancellingSeq, setCancellingSeq] = useState<number | null>(null);
  const [modal, setModal] = useState<CreditPaymentItem | null>(null);
  const [reason, setReason] = useState('');

  const openCancel = (p: CreditPaymentItem) => {
    setModal(p);
    setReason('');
  };

  const handleCancel = async () => {
    if (!modal) return;
    if (!window.confirm(
      `정말 취소하시겠어요?\n` +
      `결제 금액 ₩${modal.payAmount.toLocaleString()} 전액이 카드사로 환불되고\n` +
      `+${modal.creditAmount.toLocaleString()} 크레딧이 회수됩니다.`,
    )) return;

    setCancellingSeq(modal.purchaseCreditSeq);
    try {
      await paymentsApi.cancelCreditPayment(modal.purchaseCreditSeq, reason.trim() || undefined);
      setModal(null);
      onCancelled?.();
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || '취소 실패';
      alert('결제 취소 실패: ' + msg);
    } finally {
      setCancellingSeq(null);
    }
  };

  return (
    <>
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full table-fixed divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 w-[180px] text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  사업자
                </th>
                <th className="px-6 py-3 w-[110px] text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  원장
                </th>
                <th className="px-6 py-3 w-[200px] text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  패키지
                </th>
                <th className="px-6 py-3 w-[140px] text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  결제 금액
                </th>
                <th className="px-6 py-3 w-[120px] text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  크레딧
                </th>
                <th className="px-6 py-3 w-[110px] text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  상태
                </th>
                <th className="px-6 py-3 w-[100px] text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  수단
                </th>
                <th className="px-6 py-3 w-[180px] text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  결제일시
                </th>
                <th className="px-6 py-3 w-[140px] text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  관리
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {payments.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-6 py-12 text-center text-gray-500">
                    조건에 맞는 크레딧 결제 내역이 없어요.
                  </td>
                </tr>
              ) : (
                payments.map((p) => {
                  const meta = STATUS_META[p.paymentStatus] ?? {
                    label: p.paymentStatus,
                    cls: 'bg-gray-100 text-gray-600',
                  };
                  const isCancelling = cancellingSeq === p.purchaseCreditSeq;
                  return (
                    <tr key={p.purchaseCreditSeq} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="font-medium truncate" title={p.companyName ?? ''}>
                          {p.companyName ?? `사업장 #${p.companySeq}`}
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                          {p.region ?? ''} · SEQ {p.companySeq}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        <div className="truncate" title={p.bizUserName ?? ''}>
                          {p.bizUserName ?? '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="truncate" title={p.orderName}>
                          {p.orderName}
                        </div>
                        <div className="text-xs text-gray-400 font-mono truncate" title={p.orderId}>
                          {p.orderId}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900 tabular-nums">
                        ₩{p.payAmount.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-emerald-700 font-semibold tabular-nums">
                        +{p.creditAmount.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${meta.cls}`}>
                          {meta.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-xs text-gray-600">
                        {p.paymentMethod ?? '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDateTime(p.dtPaid)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="inline-flex items-center gap-1">
                          {p.canCancel && (
                            <button
                              type="button"
                              onClick={() => openCancel(p)}
                              disabled={isCancelling}
                              className="rounded-md border border-red-300 bg-white px-2 py-1 text-xs font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              title="7일 이내 미사용 결제 취소 — 전액 환불"
                            >
                              {isCancelling ? '취소 중…' : '결제 취소'}
                            </button>
                          )}
                          {/* 가상계좌는 PG 취소 시 환불받을 계좌 정보가 필수라 여기서 취소할 수 없다.
                              사장님 화면에는 "관리자에게 연락" 안내가 뜨므로, 문의가 오면 이 경로로 처리. */}
                          {!p.canCancel
                            && p.paymentStatus === 'COMPLETED'
                            && (p.paymentMethod ?? '').includes('가상계좌') && (
                            <span
                              className="rounded-md bg-gray-100 px-2 py-1 text-xs text-gray-600"
                              title="가상계좌 취소는 환불 계좌 정보가 필요해 앱/어드민에서 처리할 수 없습니다. 토스 대시보드에서 환불하세요."
                            >
                              토스에서 환불
                            </span>
                          )}
                          {p.pgReceiptUrl ? (
                            <a
                              href={p.pgReceiptUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-primary-600 hover:underline px-1.5"
                              title="영수증"
                            >
                              📄
                            </a>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 결제 취소 확인 모달 */}
      {modal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 px-3"
          onClick={() => setModal(null)}
        >
          <div
            className="w-full max-w-md max-h-[calc(100vh-100px)] overflow-y-auto rounded-2xl bg-white p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="mb-2 text-lg font-bold text-[#111]">결제 취소 확인</h3>
            <p className="mb-3 text-[13px] text-[#666] leading-relaxed">
              아래 결제를 취소하시겠어요?<br />
              <strong className="text-red-600">결제 금액 전액이 카드사로 환불</strong>되고
              충전된 크레딧은 회수됩니다.
            </p>

            <div className="mb-3 rounded-lg bg-gray-50 p-3 text-[13px] space-y-1">
              <div className="flex justify-between">
                <span className="text-[#666]">사업자</span>
                <span className="font-medium text-[#111]">{modal.companyName ?? `#${modal.companySeq}`}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#666]">상품</span>
                <span className="font-medium text-[#111]">{modal.orderName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#666]">환불 금액</span>
                <span className="font-bold text-red-600 tabular-nums">
                  ₩{modal.payAmount.toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#666]">회수 크레딧</span>
                <span className="font-bold tabular-nums">-{modal.creditAmount.toLocaleString()}</span>
              </div>
            </div>

            <div className="mb-3">
              <label className="text-xs font-medium text-gray-700">취소 사유 (선택)</label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                placeholder="예: 사장님 요청 — 잘못 충전"
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
              <p className="mt-1 text-xs text-gray-500">감사 로그에 영구 저장됩니다.</p>
            </div>

            <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-[12px] text-amber-800 mb-3">
              💡 7일 이내 + 충전 후 미사용 결제는 위약금 없이 100% 환불 가능합니다.
              환불 처리는 카드사에 따라 영업일 기준 3~5일 소요될 수 있어요.
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setModal(null)}
                disabled={cancellingSeq !== null}
                className="flex-1 rounded-md border border-[#D9D9D9] bg-white py-3 text-sm font-medium text-[#666] hover:bg-gray-50"
              >
                닫기
              </button>
              <button
                type="button"
                onClick={handleCancel}
                disabled={cancellingSeq !== null}
                style={{
                  backgroundColor: cancellingSeq !== null ? '#FCA5A5' : '#DC2626',
                  color: '#fff',
                  boxShadow: '0 2px 6px rgba(220, 38, 38, 0.25)',
                }}
                className="flex-[1.4] rounded-md py-3 text-sm font-bold disabled:cursor-not-allowed"
              >
                {cancellingSeq !== null ? '처리 중…' : '결제 취소 확정'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
